/**
 * The blending session's route (PLAN-BLENDING, §F3).
 *
 * A directory rather than a flat file since §F3, because the session now has a
 * Feedback page beside it; the page itself is unmoved and still router-free,
 * taking every navigation as a callback.
 *
 * **Keyed on the id.** The router reuses this component when only `$blendId`
 * changes, and a reused page keeps everything it was holding. Each blend is a
 * new mount. It was keyed for a second reason too — the workspace cache used to
 * answer a new selector with the record before it — and that half is now fixed
 * in `store/workspace-context.tsx`; `index-switch.test.tsx` is what caught it
 * here.
 *
 * **The ticket.** "Blending" returns to the log entry this session was opened
 * from rather than pushing a bare `/blending` over whatever the blender had
 * searched for (D-LS7). Everything a step deeper — a batch in the blend,
 * another session, a journal, the label sheet — is stamped with *this* entry,
 * which is still holding the log's own ticket, so Back from there lands here
 * and Back again lands on the log (D-LS8). Feedback is the one exception and
 * takes none: it comes straight back here, and here is what holds the ticket.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { BlendingPage } from '@/components/blending/blending-page';
import { useReturn, useReturnTicket } from '@/lib/return-ticket';

export const Route = createFileRoute('/blending/$blendId/')({
  component: BlendingSessionPage,
});

function BlendingSessionPage() {
  const { blendId } = Route.useParams();
  const navigate = useNavigate();
  // "Blending" goes back to the list this blend was opened from, keeping the
  // search and the scroll the blender left there; a blend reached from a link
  // or a bookmark has no such entry and pushes `/blending` as it always did
  // (PLAN-LIST-STATE D-LS7).
  const toBlending = useReturn(() => {
    void navigate({ to: '/blending' });
  });
  // One of the beers in the blend is a step deeper, so the batch page it opens
  // returns to this entry — which is still holding the list's own ticket
  // (D-LS8).
  const ticket = useReturnTicket();
  return (
    // Keyed on the id because the router reuses this component when only the
    // param changes, and a reused page keeps everything it was holding. Each
    // blend is a new mount. (The workspace cache used to need this too; that
    // half is fixed in `store/workspace-context.tsx`.)
    <BlendingPage
      key={blendId}
      blendId={blendId}
      onBack={toBlending}
      // No ticket: the calendar is a place of its own rather than a step deeper
      // into this blend, and the batch page's Next steps goes there the same way
      // (PLAN-UX-PASS E1).
      onCalendar={() => {
        void navigate({ to: '/calendar' });
      }}
      onFeedback={() => {
        void navigate({ to: '/blending/$blendId/feedback', params: { blendId } });
      }}
      // A fresh sheet with this blend on the chip row and nothing tapped yet
      // (PLAN-LABELS G4, D-LB9, D-BS9): the page is where the sheet is tapped
      // out, so there is nothing to hand it but the beer it was opened from.
      // The same ticket the batch page passes, so "Batches" from there reaches
      // the log rather than a bare list.
      onPrintLabels={() => {
        void navigate({ to: '/batches/labels', search: { beers: blendId }, state: ticket });
      }}
      onOpenBatch={(batchId) => {
        void navigate({ to: '/batches/$batchId', params: { batchId }, state: ticket });
      }}
      onOpenBlend={(next) => {
        void navigate({ to: '/blending/$blendId', params: { blendId: next }, state: ticket });
      }}
      onOpenJournal={(journalId) => {
        void navigate({ to: '/journals/$journalId', params: { journalId }, state: ticket });
      }}
    />
  );
}
